<template>
  <v-container class="ma-0 pa-0" fluid v-resize="onResize">
    <v-navigation-drawer
      v-model="filters"
      class="toolbar"
      absolute
      temporary
      width="325"
      overlay-opacity="0">
      <v-list-item :class="label">
        Filters
        <v-spacer />
        <v-btn icon small @click="filters = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-list-item>
      <v-list class="pt-6">
        <v-list-item class="listItem">
          <v-text-field
            :class="body"
            outlined
            dense
            background-color="input"
            v-model="upcFilter"
            label="UPC"
            prepend-inner-icon="mdi-filter-outline"
            @click:clear=";(upcFilter = ''), getContracts()"
            clearable />
        </v-list-item>
        <v-list-item class="listItem">
          <v-text-field
            :class="body"
            outlined
            dense
            background-color="input"
            v-model="descriptionFilter"
            label="Description"
            prepend-inner-icon="mdi-filter-outline"
            @click:clear=";(descriptionFilter = ''), getContracts()"
            clearable />
        </v-list-item>
        <v-list-item class="listItem">
          <v-autocomplete
            :class="body"
            :menu-props="{ rounded: '' }"
            :items="storeTypes"
            dense
            outlined
            item-text="storeTypeDescription"
            item-value="storeType"
            background-color="input"
            prepend-inner-icon="mdi-filter-outline"
            label="Store Type"
            clearable
            v-model="storeTypeFilter"
            @change="getContracts">
          </v-autocomplete>
        </v-list-item>
        <v-list-item class="listItem">
          <v-autocomplete
            :class="body"
            :menu-props="{ rounded: '' }"
            :items="contractTypes"
            dense
            outlined
            background-color="input"
            prepend-inner-icon="mdi-filter-outline"
            label="Contract Type"
            clearable
            v-model="contractTypeFilter"
            @change="getContracts">
          </v-autocomplete>
        </v-list-item>
        <v-list-item class="listItem">
          <v-menu
            ref="menu"
            v-model="menu"
            return-value="dates"
            :close-on-content-click="false"
            nudge-top="25"
            transition="scale-y-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :class="body"
                outlined
                dense
                background-color="input"
                v-model="dateRangeText"
                label="Date Range"
                prepend-inner-icon="mdi-filter-outline"
                v-bind="attrs"
                v-on="on"
                clearable
                @click:clear=";(dates = []), getContracts()" />
            </template>
            <v-date-picker
              :min="dateFilter[0]"
              range
              :show-current="false"
              v-model="dateFilter"
              no-title
              @click.stop="menu = false">
              <v-btn
                text
                class="text-caption text-capitalize"
                @click="dateFilter = []">
                Clear
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-list-item>
        <v-list-item class="listItem">
          <v-autocomplete
            :class="body"
            :menu-props="{ rounded: '' }"
            :items="statusFilters"
            item-text="text"
            item-value="value"
            dense
            outlined
            background-color="input"
            prepend-inner-icon="mdi-filter-outline"
            label="Status"
            clearable
            v-model="statusFilter"
            @change="getContracts">
          </v-autocomplete>
        </v-list-item>
        <v-list-item>
          <v-spacer />
          <v-btn :class="body" text @click="clearFilters">
            Clear Filters
          </v-btn>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-data-table
      :items-per-page="pageSize"
      :headers="headers"
      :height="tableHeight"
      :items="contracts"
      :custom-filter="filterDescription"
      @click:row="goToContract"
      @update:options="setSortOptions"
      :class="table"
      style="cursor: pointer"
      class="rounded-0"
      dense
      :no-data-text="
        $auth.isBuyer && !vendor
          ? 'Select Vendor to Display Contracts'
          : !$auth.isBuyer && vendor
          ? 'Create Contract for Selected Vendor'
          : 'No Contracts to Display'
      "
      fixed-header
      hide-default-footer
      :sort-by="sortOptions.sortBy"
      :sort-desc="sortOptions.sortDesc">
      <template #top>
        <v-toolbar flat color="toolbar" style="cursor: default">
          <v-col md="4" lg="3" xl="3">
            <v-autocomplete
              auto-select-first
              :class="body"
              class="mt-7"
              :menu-props="{ rounded: '', maxHeight: 515 }"
              dense
              outlined
              :items="vendors"
              item-text="vendor_name"
              item-value="vendor_number"
              background-color="input"
              :prepend-inner-icon="
                $auth.isBuyer ? 'mdi-filter-outline' : 'mdi-account-circle'
              "
              label="Vendor"
              v-model="vendor"
              return-object
              :filter="vendorFilter"
              @change="getContracts">
              <template #item="data">
                <v-list-item-content>
                  <v-list-item-title>{{
                    data.item ? data.item.vendor_name : ''
                  }}</v-list-item-title>
                  <v-list-item-subtitle class="text-capitalize">{{
                    data.item ? data.item.vendor_number : ''
                  }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-chip
                  v-if="data.item.submitted_contracts > 0"
                  class="mr-4"
                  small
                  dark
                  color="amber darken-1">
                  <span
                    class="text-capitalize"
                    style="
                      display: inline-block;
                      margin: 0 auto;
                      width: 70px;
                      text-align: center;
                    ">
                    {{ data.item.submitted_contracts }} Submitted
                  </span>
                </v-chip>
              </template>
            </v-autocomplete>
          </v-col>
          <v-btn
            :class="body"
            class="accent--text text-capitalize"
            text
            @click="filters = true">
            <v-icon left>mdi-plus</v-icon>
            Add Filter
          </v-btn>
          <v-chip
            :class="chips"
            class="ml-2"
            v-if="descriptionFilter"
            close
            @click:close=";(descriptionFilter = ''), getContracts()">
            <span class="text-capitalize">{{ descriptionFilter }}</span>
          </v-chip>
          <v-chip
            :class="chips"
            class="ml-2"
            v-if="storeTypeFilter"
            close
            @click:close=";(storeTypeFilter = ''), getContracts()">
            <span
              class="text-capitalize"
              v-for="type in storeTypes.filter(
                type => type.storeType === storeTypeFilter
              )"
              :key="type.storeType">
              {{ type.storeTypeDescription }}
            </span>
          </v-chip>
          <v-chip
            :class="chips"
            class="ml-2"
            v-if="contractTypeFilter"
            close
            @click:close=";(contractTypeFilter = ''), getContracts()">
            <span class="text-capitalize">{{ contractTypeFilter }}</span>
          </v-chip>
          <v-chip
            :class="chips"
            class="ml-2"
            v-if="dateFilter.length"
            close
            @click:close=";(dateFilter = []), getContracts()">
            <span class="text-capitalize">
              <span>{{
                moment(dateFilter[0]).format($config.date_display)
              }}</span>
              -
              <span>{{
                dateFilter[1]
                  ? moment(dateFilter[1]).format($config.date_display)
                  : ''
              }}</span>
            </span>
          </v-chip>
          <v-chip
            :class="chips"
            class="ml-4"
            v-if="statusFilter"
            close
            @click:close=";(statusFilter = ''), getContracts()">
            <span
              class="text-capitalize"
              v-for="status in statusFilters.filter(
                status => status.value === statusFilter
              )"
              :key="status.value">
              {{ status.text }}
            </span>
          </v-chip>
          <v-chip
            :class="chips"
            class="ml-2"
            v-if="upcFilter"
            close
            @click:close=";(upcFilter = ''), getContracts()">
            <span class="text-capitalize">{{ upcFilter }}</span>
          </v-chip>
          <v-spacer />
          <v-btn
            v-show="$auth.isAdmin && allVendorsSelected"
            color="primary"
            :class="body"
            class="primary white--text"
            rounded
            @click=";(component = 'POSExports'), (modal = true)">
            <v-icon left> mdi-export-variant</v-icon>Generate POS
          </v-btn>
          <v-btn
            :disabled="allVendorsSelected || !vendor"
            color="primary"
            :class="body"
            class="primary white--text ml-2"
            rounded
            @click="goToContract">
            <v-icon left>mdi-plus</v-icon>Create
          </v-btn>
        </v-toolbar>
      </template>

      <template #[`header.vendor_name`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.description`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.store_type`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.contract_types`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.item_count`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.ad_dates`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.tpr_dates`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.cost_change_date`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.new_item_date`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.status`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>
      <template #[`header.print`]="{ header }">
        <span class="noWrap">{{ header.text }}</span>
      </template>

      <template #[`item.vendor_name`]="{ item }">
        <span class="noWrap">{{ item.vendor_name }}</span>
      </template>
      <template #[`item.description`]="{ item }">
        <span class="noWrap">{{ item.description }}</span>
      </template>
      <template #[`item.store_type`]="{ item }">
        <span
          class="noWrap"
          v-for="type in storeTypes.filter(
            type => type.storeType === item.store_type
          )"
          :key="type.storeType">
          {{ type.storeTypeDescription }}
        </span>
      </template>
      <template #[`item.contract_types`]="{ item }">
        <span
          class="noWrap"
          v-for="(type, index) in item.contract_types"
          :key="index"
          >{{ type }}</span
        >
      </template>
      <template #[`item.item_count`]="{ item }">
        <span class="noWrap">{{
          item.item_count ? item.item_count : '---'
        }}</span>
      </template>
      <template #[`item.ad_start`]="{ item }">
        <span class="noWrap">{{
          item.ad_start
            ? `${formatDateDisplay(item.ad_start)} - ${formatDateDisplay(
                item.ad_end
              )}`
            : '---'
        }}</span>
      </template>
      <template #[`item.tpr_start`]="{ item }">
        <span class="noWrap">{{
          item.tpr_start
            ? `${formatDateDisplay(item.tpr_start)} - ${formatDateDisplay(
                item.tpr_end
              )}`
            : '---'
        }}</span>
      </template>
      <template #[`item.cost_change_date`]="{ item }">
        <span class="noWrap">{{
          item.cost_change_date
            ? `${formatDateDisplay(item.cost_change_date)}`
            : '---'
        }}</span>
      </template>
      <template #[`item.new_item_date`]="{ item }">
        <span class="noWrap">{{
          item.new_item_date
            ? `${formatDateDisplay(item.new_item_date)}`
            : '---'
        }}</span>
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip class="mr-4" small dark :color="getColor(item.status)">
          <v-icon left small>{{ getIcon(item.status) }}</v-icon>
          <span
            class="text-capitalize"
            style="
              display: inline-block;
              margin: 0 auto;
              width: 90px;
              text-align: center;
            ">
            <span>{{ getStatusName(item.status) }}</span>
          </span>
        </v-chip>
      </template>
      <template #[`item.print`]="{ item }">
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn
              v-on="on"
              icon
              @click.stop="contractToExcel(item.contractuuid)">
              <v-icon>mdi-file-download</v-icon>
            </v-btn>
          </template>
          <span>Print</span>
        </v-tooltip>
        <!-- <v-chip class="mr-4" small dark color='Grey' @click.stop="contractToExcel(item.contractuuid)" >
            <v-icon left small>mdi-cash-register</v-icon>
              <span>Print</span>
          </v-chip> -->
      </template>

      <template #footer>
        <v-divider />
        <v-toolbar height="59" flat style="cursor: default">
          <v-row dense align="center" justify="end" style="height: 59px">
            <v-spacer />
            <v-col xs="2" sm="2" md="2" lg="2" xl="2" align="end">
              <span class="text-caption">Rows per page:</span>
            </v-col>
            <v-col xs="2" sm="2" md="2" lg="1" xl="1">
              <v-select
                class="text-caption mx-6 mt-2"
                :items="pageSizes"
                v-model="pageSize">
              </v-select>
            </v-col>
            <v-col
              xs="2"
              sm="2"
              md="2"
              lg="1"
              xl="1"
              align="end"
              style="max-width: 100px">
              <v-btn
                :disabled="prev"
                height="36"
                width="36"
                icon
                class="mb-2 ml-1 mr-1"
                @click="page -= 1">
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                :disabled="next"
                height="36"
                width="36"
                icon
                class="mb-2 ml-1"
                @click="page += 1">
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-toolbar>
      </template>
    </v-data-table>

    <component
      :is="component"
      :key="componentKey"
      :value.sync="modal"
      @confirm="getPOSExports"
      :contract="contract" />
    <!-- <ContractCopy :value.sync="modal" :contract="contract"/> -->
    <Overlay />
    <!-- <POSExportsAlert> -->
  </v-container>
</template>

<script>
import { table } from '@/mixins/table-config.js'
import { format } from '@/mixins/format'
import { displayHelpers } from '@/mixins/display-helpers'
import { mapState } from 'vuex'
import { debounce } from 'lodash'

export default {
  name: 'ContractList',

  components: {
    ContractCopy: () => import('@/components/contract/ContractCopy'),
    Overlay: () => import('@/components/shared/Overlay'),
    POSExports: () => import('@/components/contract/alerts/POSExports')
  },

  mixins: [table, format, displayHelpers],

  data: () => ({
    component: '',
    componentKey: 0,
    contract: {},
    contracts: [],
    contractTypeFilter: '',
    contractView: false,
    dateFilter: [],
    descriptionFilter: '',
    upcFilter: '',
    disableNext: false,
    disablePrevious: true,
    filters: false,
    hasActiveClusters: true,
    limit: '',
    menu: false,
    modal: false,
    offset: '',
    page: 1,
    pageSize: 250,
    pageSizes: [50, 100, 250],
    statusFilter: '',
    storeTypeFilter: '',
    tableHeight: '',
    sortOptions: {
      sortBy: '',
      sortDesc: false
    },
    vendor: {},
    vendors: []
  }),

  computed: {
    user() {
      return this.$auth.user
    },

    ...mapState(['storeTypes', '$auth.isBuyer']),

    filtersApplied() {
      return (
        !this._.isEmpty(this.status) ||
        !this._.isEmpty(this.contractType) ||
        !this._.isEmpty(this.dates)
      )
    },

    dateRangeText() {
      const dates = [
        this.dateFilter.length
          ? this.moment(this.dateFilter[0]).format(this.$config.date_display)
          : '',
        this.dateFilter.length
          ? this.moment(this.dateFilter[1]).format(this.$config.date_display)
          : ''
      ]
      return this.dateFilter[0] && this.dateFilter[1] ? dates.join(' - ') : ''
    },

    minDate() {
      return this.dateFilter.length && this.dateFilter[0]
        ? this.dateFilter[0]
        : ''
    },

    statusFilters() {
      return [
        { text: 'Draft', value: 'draft' },
        { text: 'Submitted', value: 'submitted' },
        { text: 'Approved', value: 'approved' },
        { text: 'Declined', value: 'declined' },
        { text: 'POS Pending', value: 'pos_pending' },
        { text: 'POS Generated', value: 'pos_generated' }
      ]
    },

    contractTypes() {
      return ['Ad', 'TPR', 'Cost Change', 'New Item']
    },

    allVendorsSelected() {
      return !this._.isEmpty(this.vendor)
        ? this.vendor.vendor_number === 'ALL_VENDORS'
        : false
    },

    headers() {
      const headers = [
        { sortable: false, class: 'accent white--text', width: '2%' },
        {
          text: 'Description',
          align: 'start',
          class: 'label accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: true,
          value: 'description',
          width: 'auto'
        },
        {
          text: 'Store Type',
          align: 'start',
          class: 'accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: true,
          value: 'store_type',
          width: 'auto'
        },
        {
          text: 'Contract Types',
          align: 'start',
          class: 'accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: true,
          value: 'contract_types',
          width: 'auto'
        },
        // { text: 'Items', align: 'center', class: 'accent font-weight-medium white--text', filterable: true, sortable: true, value: 'item_count', width: '100' },
        {
          text: 'Ad Start - Ad End',
          align: 'center',
          class: 'accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: true,
          value: 'ad_start',
          width: 'auto'
        },
        {
          text: 'TPR Start - TPR End',
          align: 'center',
          class: 'accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: true,
          value: 'tpr_start',
          width: 'auto'
        },
        {
          text: 'Cost Change Date',
          align: 'center',
          class: 'accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: true,
          value: 'cost_change_date',
          width: '160'
        },
        {
          text: 'New Item Date',
          align: 'center',
          class: 'accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: true,
          value: 'new_item_date',
          width: '150'
        },
        {
          text: 'Status',
          align: 'center',
          class: 'accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: true,
          value: 'status',
          width: 'auto'
        },
        {
          text: 'Print',
          align: 'center',
          class: 'accent font-weight-medium white--text noWrap',
          filterable: true,
          sortable: false,
          value: 'print',
          width: 'auto'
        }
      ]

      const vendorNameHeader = {
        text: 'Vendor Name',
        align: 'start',
        class: 'label accent font-weight-medium white--text noWrap',
        filterable: true,
        sortable: true,
        value: 'vendor_name',
        width: 'auto'
      }

      if (!this._.isEmpty(this.vendor)) {
        if (this.vendor.vendor_number == 'ALL_VENDORS') {
          headers.splice(1, 0, vendorNameHeader)
        }
      }

      return headers
    },

    prev() {
      return this.page === 1
    },

    next() {
      return this.contracts.length < this.pageSize
    }
  },

  watch: {
    user: {
      handler(newValue) {
        if (newValue) {
          this.getVendors()
        }
      },
      immediate: true
    },

    vendor: {
      handler(newValue) {
        if (newValue) {
          localStorage.setItem('filter_vendor', JSON.stringify(newValue))
          this.$store.commit('SET_CURRENT_VENDOR', newValue)
        } else {
          this.contracts = []
        }
      }
    },

    page: {
      handler() {
        this.loading = true
        this.getContracts()
      }
    },

    pageSize: {
      handler() {
        this.page = 1
        this.loading = true
        this.getContracts()
      }
    },

    filters: {
      immediate: true,
      handler() {
        if (this.filters) {
          this.$emit('close')
        }
      }
    },

    upcFilter: debounce(function () {
      this.page = 1
      if (this.upcFilter) {
        localStorage.setItem('filter_upc', this.upcFilter)
        this.getContracts()
      } else {
        localStorage.removeItem('filter_upc')
      }
    }, 1000),

    descriptionFilter: debounce(function () {
      this.page = 1
      if (this.descriptionFilter) {
        localStorage.setItem('filter_description', this.descriptionFilter)
        this.getContracts()
      } else {
        localStorage.removeItem('filter_description')
      }
    }, 1000),

    storeTypeFilter: {
      handler() {
        this.page = 1
        if (this.storeTypeFilter) {
          localStorage.setItem('filter_store_type', this.storeTypeFilter)
        } else {
          localStorage.removeItem('filter_store_type')
        }
      }
    },

    contractTypeFilter: {
      handler() {
        this.page = 1
        if (this.contractTypeFilter) {
          localStorage.setItem('filter_contract_type', this.contractTypeFilter)
        } else {
          localStorage.removeItem('filter_contract_type')
        }
      }
    },

    dateFilter: {
      handler() {
        this.page = 1
        if (this.dateFilter.length) {
          if (this.dateFilter[1]) {
            this.getContracts()
            this.menu = false
          }
          localStorage.setItem('filter_dates', JSON.stringify(this.dateFilter))
        } else {
          localStorage.removeItem('filter_dates')
        }
      }
    },

    statusFilter: {
      handler() {
        this.page = 1
        if (this.statusFilter) {
          localStorage.setItem('filter_status', this.statusFilter)
        } else {
          localStorage.removeItem('filter_status')
        }
      }
    },

    sortOptions: {
      handler(newValue) {
        if (newValue) {
          localStorage.setItem('sort_options', JSON.stringify(this.sortOptions))
        } else {
          localStorage.removeItem('sort_options')
        }
      },
      deep: true
    },

    modal: {
      immediate: true,
      handler() {
        this.componentKey += 1
      }
    }
  },

  created() {
    if (!this.$auth.roles.length) {
      this.$router.push('/redirect')
    }
    this.tableHeight = window.innerHeight - 225
    localStorage.removeItem('contract_uuid')
    const {
      filter_vendor,
      filter_description,
      filter_store_type,
      filter_contract_type,
      filter_dates,
      filter_status,
      filter_upc,
      sort_options
    } = localStorage

    if (filter_vendor) this.vendor = JSON.parse(filter_vendor)
    if (filter_description) this.descriptionFilter = filter_description
    if (filter_store_type) this.storeTypeFilter = filter_store_type
    if (filter_contract_type) this.contractTypeFilter = filter_contract_type
    if (filter_dates) this.dateFilter = JSON.parse(filter_dates)
    if (filter_status) this.statusFilter = filter_status
    if (filter_upc) this.upcFilter = filter_upc
    if (sort_options) {
      const { sortBy, sortDesc } = JSON.parse(sort_options)
      this.sortOptions = {
        sortBy: sortBy,
        sortDesc: sortDesc
      }
    }

    this.$store.commit('SET_READONLY', null)
    this.$store.commit('SET_CONTRACT_STATUS', '')
    this.$store.commit('SET_CONTRACT_TYPES', [])
    this.$store.commit('SET_COMMENTS', [])
    this.$store.commit('SET_ALL_STORES', [])
    this.$store.commit('SET_ADDED_ITEMS', '')

    this.$store.dispatch('getStoreTypes')
  },

  methods: {
    setSortOptions(value) {
      this.sortOptions.sortDesc = value.sortDesc[0]
      this.sortOptions.sortBy = value.sortBy[0]
    },

    onResize() {
      this.tableHeight = window.innerHeight - 225
    },

    clearFilters() {
      this.descriptionFilter = ''
      this.storeTypeFilter = ''
      this.contractTypeFilter = ''
      this.dateFilter = []
      this.statusFilter = ''
      this.upcFilter = ''
      this.getContracts()
    },

    vendorFilter(item, queryText) {
      const name = item.vendor_name.toLowerCase()
      const number = item.vendor_number.toLowerCase()
      const searchText = queryText.toLowerCase()

      return name.indexOf(searchText) > -1 || number.indexOf(searchText) > -1
    },

    filterDescription(description, item) {
      return (
        item &&
        description &&
        item.description.toString().toLowerCase().indexOf(description) !== -1
      )
    },

    itemRowBackground(item) {
      return this.contracts.indexOf(item) % 2 !== 1 ? 'grey lighten-2' : ''
    },

    getIcon(status) {
      switch (status) {
        case 'draft':
          return 'mdi-progress-pencil'
        case 'submitted':
          return 'mdi-progress-check'
        case 'approved':
          return 'mdi-checkbox-marked-circle-outline'
        case 'declined':
          return 'mdi-progress-alert'
        case 'pos_pending':
          return 'mdi-progress-clock'
        case 'pos_generated':
          return 'mdi-cash-register'
      }
    },

    getStatusName(status) {
      switch (status) {
        case 'draft':
          return 'Draft'
        case 'submitted':
          return 'Submitted'
        case 'approved':
          return 'Approved'
        case 'declined':
          return 'Declined'
        case 'pos_pending':
          return 'POS Pending'
        case 'pos_generated':
          return 'POS Generated'
      }
    },

    getColor(status) {
      switch (status) {
        case 'draft':
          return 'blue-grey lighten-1'
        case 'submitted':
          return 'amber darken-1'
        case 'approved':
          return 'green'
        case 'declined':
          return 'red'
        case 'pos_pending':
          return 'deep-orange darken-1'
        case 'pos_generated':
          return 'black'
      }
    },

    async getVendors() {
      this.$store.dispatch('setOverlay', { status: 'show', text: 'Loading...' })
      const request = {
        vendor_numbers: !this.$auth.isBuyer ? this.$auth.vendorNumbers : []
      }
      try {
        const { data } = await this.$vendors.getVendorInfo(request)
        this.vendors = data
        this.$store.commit('SET_VENDORS', this.vendors)
        if (this.$auth.isBuyer) {
          // this.vendors.unshift({ vendor_name: 'ALL VENDORS', vendor_number: 'ALL_VENDORS'})
          this.vendors = [
            { vendor_name: 'ALL VENDORS', vendor_number: 'ALL_VENDORS' },
            ...data
          ]
        }
        if (!this.vendor || !localStorage.filter_vendor)
          this.vendor = this.vendors[0]
        this.getContracts()
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Retrieving Vendors'
        })
      }
    },

    async getContracts() {
      this.$store.dispatch('setOverlay', { status: 'show', text: 'Loading...' })
      this.limit = this.pageSize
      this.offset = (this.page - 1) * this.pageSize
      try {
        const response = await this.$contracts.getContracts(
          this.vendor.vendor_number,
          this.offset,
          this.limit,
          this.descriptionFilter,
          this.storeTypeFilter,
          this.contractTypeFilter,
          this.dateFilter,
          this.statusFilter,
          this.upcFilter
        )
        this.contracts = response.data
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Retrieving Contracts'
        })
      } finally {
        this.loading = false
        this.$store.dispatch('setOverlay', { status: 'hide' })
      }
    },

    async contractToExcel(contractUUID) {
      this.$store.dispatch('setOverlay', {
        status: 'show',
        text: 'Generating...'
      })
      await this.$contracts
        .printContract(contractUUID)
        .then(() => {
          this.$store.dispatch('setSnackbar', {
            status: 'success',
            text: 'Excel Files Generated'
          })
          setTimeout(() => {
            console.log('Generating Excel Files!')
          }, 5000)
        })
        .catch(() => {
          this.eventbus.$emit('error')
          this.$store.dispatch('setSnackbar', {
            status: 'error',
            text: 'Unexpected Error'
          })
        })

      this.loading = false
      this.$store.dispatch('setOverlay', { status: 'hide' })
    },

    async getPOSExports() {
      this.$store.dispatch('setOverlay', {
        status: 'show',
        text: 'Generating...'
      })
      await this.$contracts
        .generatePOS()
        .then(() => {
          this.$store.dispatch('setSnackbar', {
            status: 'success',
            text: 'POS Exports Generated'
          })
          setTimeout(() => {
            console.log('Generating POS!')
          }, 5000)
        })
        .catch(() => {
          this.eventbus.$emit('error')
          this.$store.dispatch('setSnackbar', {
            status: 'error',
            text: 'Unexpected Error'
          })
        })

      this.loading = false
      this.$store.dispatch('setOverlay', { status: 'hide' })
      this.getContracts()
    },

    async checkActiveClusters() {
      try {
        const response = await this.$contracts.getActiveClusters()
        this.hasActiveClusters = response.data
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Checking Active Clusters'
        })
        clearInterval(this.polling)
      }
    },

    async goToContract(contract) {
      const readonly =
        !this.$auth.isBuyer &&
        contract.status !== 'draft' &&
        contract.status !== 'declined'
      this.$store.commit('SET_READONLY', readonly)
      try {
        this.$router.push({
          name: 'ContractView',
          params: { contractUUID: contract.contractuuid, vendor: this.vendor },
          query: { id: contract.contractuuid }
        })
      } catch {
        this.$store.dispatch('setSnackbar', {
          status: 'error',
          text: 'Error Opening Contract'
        })
      }
    }
  }
}
</script>
<style>
.theme--light.v-data-table
  .v-data-table-header
  th.sortable
  .v-data-table-header__icon {
  color: #fff !important;
}

.noWrap {
  white-space: nowrap;
}

.listItem {
  height: 60px;
}
</style>
